import type { PageType } from './page-context.js';

/**
 * Widget settings from Laravel API:
 * GET /api/get-modules-settings?company_id={id}&module_type=300
 *
 * All values arrive as strings from the backend.
 * JSON fields are serialized strings that must be parsed client-side.
 */

/** Launcher position on the host page */
export type LauncherPosition =
  | 'bottom_left'
  | 'bottom_right'
  | 'top_left'
  | 'top_right';

/**
 * Mobile-only launcher interaction mode (`BS_MOBILE_LAUNCHER_MODE`).
 * Desktop always uses fixed FAB regardless of this setting.
 */
export type MobileLauncherMode = 'fixed' | 'draggable' | 'edge';

/** Default: current fixed FAB (backward compatible). */
export const DEFAULT_MOBILE_LAUNCHER_MODE: MobileLauncherMode = 'fixed';

/** Chat window size preset */
export type WidgetSize = 'small' | 'medium' | 'large';

/**
 * i18n map: locale code → text.
 * Example: { "uk": "Вітаємо...", "en": "Welcome..." }
 */
export type I18nJson = Record<string, string>;

/**
 * Schedule per weekday (1 = Monday … 7 = Sunday).
 * Empty start/end "00:00" means always available.
 */
export type MessageSchedule = Record<
  string,
  Array<{ start: string; end: string }>
>;

/**
 * Per-page proactive popup delay (seconds). `"0"` = disabled for that page type.
 * Keys match `PageType`; admin may use alias `catalog` → `category`.
 */
export type ContextPopupDelayByPageType = Partial<Record<PageType, number>>;

/**
 * How often to show proactive popups relative to chat engagement.
 * - `first_dialog_reengage` — every page until chat interaction; then after gap/dwell.
 * - `every_page` — legacy: every pathname visit regardless of chat interaction.
 */
export type ProactiveGreetingMode = 'first_dialog_reengage' | 'every_page';

/** Default: suppress after chat open until re-engage gap (30 minutes). */
export const DEFAULT_PROACTIVE_GREETING_MODE: ProactiveGreetingMode =
  'first_dialog_reengage';

/** Default re-engage / dwell gap: 30 minutes. */
export const DEFAULT_PROACTIVE_GREETING_REENGAGE_SECONDS = 1800;

/**
 * Full widget settings response from backend.
 * Keys marked @admin-new are proposed — connect in admin panel.
 */
export interface WidgetSettingsResponse {
  // ─── Launcher (bubble) ───────────────────────────────────────────
  /** Launcher button background color */
  BS_BG_COLOR_MESSAGES: string;
  /** Launcher border radius in px (50 = circle when size equals diameter) */
  BS_BORDER_RADIUS: string;
  /** Bottom offset in px */
  BS_INDENT_FROM_BELOW: string;
  /** Pre-chat popup background */
  BS_POPUP_BG_COLOR: string;
  BS_POPUP_BUTTON_BG_COLOR: string;
  BS_POPUP_BUTTON_TEXT_COLOR: string;
  BS_POPUP_TEXT_COLOR: string;
  /** Launcher position: bottom_left | bottom_right | … */
  BS_POSITIONING: LauncherPosition;
  /**
   * Mobile launcher UX: fixed | draggable | edge.
   * Desktop ignores this key. Omitted → fixed.
   */
  BS_MOBILE_LAUNCHER_MODE?: string;
  /** Side offset from viewport edge in px */
  BS_RETREAT_FROM_EDGE: string;
  /** Launcher button diameter in px */
  BS_SIZE: string;
  BS_TEXT_COLOR: string;

  // ─── Chat window ─────────────────────────────────────────────────
  WS_BG_COLOR: string;
  WS_BG_COLOR_HEADER: string;
  /** Incoming messages (bot / AI / manager) bubble background */
  WS_BG_COLOR_MESSAGES: string;
  /**
   * Outgoing messages (user) bubble background.
   * @admin-new — fallback: 12% tint of WS_TEXT_COLOR on WS_BG_COLOR
   */
  WS_BG_COLOR_USER_MESSAGES?: string;
  WS_BORDER_RADIUS: string;
  /** Font family; may inherit from host site when set to "inherit" */
  WS_FONT_FAMILY: string;
  /** General UI text (input, footer, separators) — not message bubbles */
  WS_TEXT_COLOR: string;
  WS_TEXT_COLOR_HEADER: string;
  /**
   * Incoming message bubble text color.
   * @admin-new — fallback: WS_TEXT_COLOR
   */
  WS_TEXT_COLOR_MESSAGES?: string;
  /**
   * Outgoing (user) message bubble text color.
   * @admin-new — fallback: WS_TEXT_COLOR
   */
  WS_TEXT_COLOR_USER_MESSAGES?: string;
  WS_WIDGET_SIZE: WidgetSize;

  // ─── Content & behavior ──────────────────────────────────────────
  /**
   * "true" | "false" — mount the embeddable widget on the customer site.
   * When "false", loader authenticates/loads settings but does not render UI.
   * Omitted → treated as visible (`true`) for backward compatibility.
   */
  WIDGET_VISIBLE?: string;
  /**
   * "true" | "false" — collect rich page context (parser, behavior watchers,
   * product signals for analytics / send). Independent from proactive greeting.
   */
  CONTEXT_ENABLED: string;
  /**
   * "true" | "false" — proactive greeting: timed popup, exit intent popup,
   * AI welcome in chat via `POST /api/widget/{slug}/greeting`.
   * Falls back to `CONTEXT_ENABLED` when omitted (legacy backends).
   * @admin-new
   */
  PROACTIVE_GREETING_ENABLED?: string;
  /** Comma-separated locale codes: "uk,en,de" */
  LANGUAGES: string;
  /** JSON string → MessageSchedule */
  MESSAGE_SCHEDULE: string;
  SHOP_NAME: string;
  URL: string;
  /** JSON string → I18nJson — first message in chat */
  WELCOME_MESSAGE: string;

  /**
   * Exit-intent popup message on homepage (cursor leaves viewport top).
   * JSON string → I18nJson
   * @admin-new
   */
  EXIT_INTENT_MESSAGE: string;

  /**
   * AI agent display name in header and message attribution.
   * @admin-new — optional; fallback: parse from WELCOME_MESSAGE or "Teo"
   */
  AI_AGENT_NAME?: string;

  /**
   * AI agent avatar URL. Empty / missing → default bot avatar SVG.
   * @admin-new
   */
  AI_AVATAR_URL?: string;

  /**
   * Default proactive popup delay in seconds (fallback when page type is not in map).
   * "0" = disabled when no per-type override exists.
   * @admin-new
   */
  CONTEXT_POPUP_DELAY_SECONDS?: string;

  /**
   * Per-page-type proactive popup delays (JSON string).
   * Example: `{"home":10,"category":15,"product":12,"cart":14}`
   * Keys: `home` | `product` | `category` | `cart` | `checkout` | `blog` | `search` | `other`
   * Alias: `catalog` → `category`. `"0"` = no timed popup on that page type.
   * Same delays apply when `PROACTIVE_GREETING_AUTO_OPEN_CHAT` is true.
   * @admin-new
   */
  CONTEXT_POPUP_DELAY_BY_PAGE_TYPE?: string;

  /**
   * "true" | "false" — after the proactive greeting delay, open the full chat
   * window instead of showing the popup bubble. Timing uses
   * `CONTEXT_POPUP_DELAY_BY_PAGE_TYPE` / `CONTEXT_POPUP_DELAY_SECONDS`.
   * Requires `PROACTIVE_GREETING_ENABLED`. Exit intent is unchanged (still popup).
   * @admin-new
   */
  PROACTIVE_GREETING_AUTO_OPEN_CHAT?: string;

  /**
   * "true" | "false" — greeting popup actions.
   * `"true"`: quick-reply chips (no CTA). `"false"` / omitted: "open chat" button (no chips).
   * Exit-intent popup always keeps the CTA.
   * @admin-new
   */
  PROACTIVE_GREETING_QUICK_REPLIES?: string;

  /**
   * "1" | "2" | "3" — how many quick-reply chips when `PROACTIVE_GREETING_QUICK_REPLIES` is true
   * and source is `ai`. Default `"3"`. Sent to `POST /greeting` as `quick_reply_count`.
   * @admin-new
   */
  PROACTIVE_GREETING_QUICK_REPLY_COUNT?: string;

  /**
   * `"ai"` (default) | `"custom"` — chip source when quick replies are enabled.
   * `custom`: one chip from `PROACTIVE_GREETING_CUSTOM_QUICK_REPLY` (no `quick_reply_count` in greeting).
   * @admin-new
   */
  PROACTIVE_GREETING_QUICK_REPLY_SOURCE?: string;

  /**
   * JSON i18n map for the custom single chip label+message.
   * Example: `{"uk":"Підкажіть","en":"Help me"}`
   * @admin-new
   */
  PROACTIVE_GREETING_CUSTOM_QUICK_REPLY?: string;

  /**
   * Proactive popup frequency relative to chat engagement (not thread_id).
   * - `first_dialog_reengage` (default) — every page until the visitor opens chat;
   *   after chat interaction: only re-engage after gap / long dwell, then repeat.
   * - `every_page` — legacy: timed popup on every pathname visit even after chat open.
   * @admin-new
   */
  PROACTIVE_GREETING_MODE?: string;

  /**
   * Seconds before re-engage when mode is `first_dialog_reengage` after chat interaction.
   * Applies to return-visit gap and continuous on-site dwell. Default `1800` (30 minutes).
   * @admin-new
   */
  PROACTIVE_GREETING_REENGAGE_SECONDS?: string;

  /**
   * "true" | "false" — show AI disclosure footer under the input.
   * @admin-new — default "false" if absent
   */
  POWERED_BY_ENABLED?: string;

  /**
   * Brand text override. Empty → localized short AI label in widget UI.
   * @admin-new
   */
  POWERED_BY_TEXT?: string;

  /**
   * Link target for powered-by branding
   * @admin-new
   */
  POWERED_BY_URL?: string;
}

/** Parsed settings ready for UI consumption */
export interface WidgetSettings {
  launcher: {
    sizePx: number;
    borderRadiusPx: number;
    backgroundColor: string;
    textColor: string;
    position: LauncherPosition;
    offsetX: number;
    offsetY: number;
    /** Mobile-only; desktop always behaves as fixed. */
    mobileMode: MobileLauncherMode;
  };
  popup: {
    backgroundColor: string;
    textColor: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
  };
  window: {
    size: WidgetSize;
    borderRadiusPx: number;
    backgroundColor: string;
    headerBackgroundColor: string;
    headerTextColor: string;
    incomingMessageBackgroundColor: string;
    incomingMessageTextColor: string;
    outgoingMessageBackgroundColor: string;
    outgoingMessageTextColor: string;
    textColor: string;
    fontFamily: string;
    widthPx: number;
    heightPx: number;
    mobileBreakpointPx: number;
  };
  content: {
    shopName: string;
    widgetUrl: string;
    welcomeMessage: I18nJson;
    exitIntentMessage: I18nJson;
    languages: string[];
    messageSchedule: MessageSchedule;
    /** When false, loader must not mount the widget UI. Default true. */
    widgetVisible: boolean;
    contextEnabled: boolean;
    proactiveGreetingEnabled: boolean;
    aiAgentName: string;
    aiAvatarUrl: string | null;
    contextPopupDelaySeconds: number;
    contextPopupDelayByPageType: ContextPopupDelayByPageType;
    proactiveGreetingAutoOpenChat: boolean;
    /** Greeting popup: chips instead of the open-chat CTA. */
    proactiveGreetingQuickReplies: boolean;
    /** Chip count when quick replies are enabled (1–3). Default 3. */
    proactiveGreetingQuickReplyCount: 1 | 2 | 3;
    /** Chip source: AI `/greeting` list or one cabinet-authored chip. */
    proactiveGreetingQuickReplySource: 'ai' | 'custom';
    /** Localized text for the custom single chip (label = message). */
    proactiveGreetingCustomQuickReply: I18nJson;
    proactiveGreetingMode: ProactiveGreetingMode;
    proactiveGreetingReengageSeconds: number;
    poweredBy: {
      enabled: boolean;
      text: string;
      url: string;
    };
  };
}

export const WIDGET_SIZE_DIMENSIONS: Record<
  WidgetSize,
  { width: number; height: number }
> = {
  small: { width: 340, height: 520 },
  medium: { width: 380, height: 600 },
  large: { width: 420, height: 680 },
};

export const MOBILE_BREAKPOINT_PX = 768;

/** Default when backend omits `CONTEXT_POPUP_DELAY_SECONDS`. */
export const DEFAULT_CONTEXT_POPUP_DELAY_SECONDS = 15;

/**
 * Max wait for greeting API after the configured popup delay elapses.
 * Popup shows at: delay + [0 … this value] depending on API latency.
 */
export const GREETING_POPUP_MAX_WAIT_MS = 15_000;

export const DEFAULT_POWERED_BY = {
  /** Empty → localized `aiPoweredLabel` in footer when enabled. */
  text: '',
  url: 'https://aiteo.tech',
} as const;
